import React, { useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import "../../assets/styles/chart.css";
import { useViewGraphQuery } from "../../hooks/dashboard/graph.view.all";
import { mapUserActivityGraph } from "../../utils/mapUserActivityGraph";

const LineChartComponent = ({
  data: propData,
  lines: propLines = [],
  xKey: propXKey = "name",
  title: propTitle = "User Activity",
  subtitle: propSubtitle = "Monthly user growth trend",
}) => {
  const fetchFromApi = propData == null;

  const { data: fetchedData, isLoading, isError } = useViewGraphQuery({
    enabled: fetchFromApi,
  });

  const chartConfig = useMemo(() => {
    if (fetchFromApi) {
      return (
        mapUserActivityGraph(fetchedData, {
          lines: propLines,
          title: propTitle,
          subtitle: propSubtitle,
        }) ?? {
          data: [],
          lines: [],
          xKey: propXKey,
          title: propTitle,
          subtitle: propSubtitle,
        }
      );
    }

    return {
      data: propData ?? [],
      lines: propLines,
      xKey: propXKey,
      title: propTitle,
      subtitle: propSubtitle,
    };
  }, [
    fetchFromApi,
    fetchedData,
    propData,
    propLines,
    propXKey,
    propTitle,
    propSubtitle,
  ]);

  const { data, lines, xKey, title, subtitle } = chartConfig;
  const minChartWidth = Math.max(data.length * 80, 500);

  if (fetchFromApi && isLoading) {
    return <div className="line-chart-card chart-loading">Loading chart...</div>;
  }

  if (fetchFromApi && isError) {
    return (
      <div className="line-chart-card chart-error">
        Could not load chart data.
      </div>
    );
  }

  if (!data?.length || !lines?.length) {
    return (
      <div className="line-chart-card chart-empty">No data available</div>
    );
  }

  return (
    <div className="line-chart-card">
      <div>
        <h3 className="chart-title">{title}</h3>
        {subtitle && <p className="chart-subtitle">{subtitle}</p>}
      </div>

      <div className="chart-wrapper">
        <div style={{ minWidth: minChartWidth }}>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
              <XAxis dataKey={xKey} stroke="#6b7280" />
              <YAxis stroke="#6b7280" domain={[0, "auto"]} allowDecimals={false} />
              <Tooltip contentStyle={{ backgroundColor: "#ffffff", border: "1px solid #e5e7eb", borderRadius: "8px" }} />
              <Legend />

              {lines.map((line, index) => (
                <Line
                  key={index}
                  type="monotone"
                  dataKey={line.dataKey}
                  name={line.name}
                  stroke={line.stroke || "#5C308D"}
                  strokeWidth={2}
                  connectNulls
                  dot={{ r: 4, fill: line.dotColor || line.stroke }}
                  activeDot={{ r: 6 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default LineChartComponent;
